/* Plant Live — control-room board for the Batcher Cockpit.
   Left: cement silos · Centre: loading bay · Right: aggregate bins · Bottom: additive tanks.
   Levels are real inventory: tonnes on bulk, litres on additives, and every manual
   correction writes through the stock-adjust endpoint so movements stay auditable. */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, Pencil } from "lucide-react";

const SILO_CAP_KG = 60000;
const HERO_IMG = "/images/plant-board-v2.jpg";
// Hotspots measured off plant-board.jpg (percent of the 1264x848 frame).
const SILO_BOX = [
  { x: 6.7, y: 10.6, w: 12.3, h: 21.8, plate: { x: 7.9, y: 19.8, w: 9.9, h: 5.6 } },
  { x: 19.8, y: 10.6, w: 11.4, h: 21.8, plate: { x: 21.0, y: 19.8, w: 9.0, h: 5.6 } },
];
// glass bins: sand + dust on the upper shelf, four graded aggregate cylinders below (20→14→10→7)
const BIN_BOX = [
  { x: 13.5, y: 62.0 }, { x: 26.8, y: 62.0 },
  { x: 8.7, y: 79.0 }, { x: 17.6, y: 79.0 }, { x: 26.1, y: 79.0 }, { x: 34.7, y: 79.0 },
];
// eight admixture tanks along the gantry
const TANK_BOX = [37.6, 45.5, 53.2, 61.1, 69.0, 76.7, 84.5, 92.4];
const WATER_CAP_L = 65000;
// Bins are built from the plant's own bulk stock, in batching order — a plant with no
// 10 mm simply doesn't get a 10 mm cylinder.
const BIN_DEFS = [
  { key: "sand", cat: "sand", label: "SAND", cap: 60000, colour: "#EAB308", slot: 0 },
  { key: "man_dust", cat: "man_dust", label: "DUST", cap: 40000, colour: "#A16207", slot: 1 },
  { key: "agg_20mm", cat: "aggregate_20mm", label: "20 MM", cap: 80000, colour: "#3B82F6", slot: 2 },
  { key: "agg_14mm", cat: "aggregate_14mm", label: "14 MM", cap: 40000, colour: "#0EA5E9", slot: 3 },
  { key: "agg_10mm", cat: "aggregate_10mm", label: "10 MM", cap: 40000, colour: "#6366F1", slot: 4 },
  { key: "agg_7mm", cat: "aggregate_7mm", label: "7 MM", cap: 40000, colour: "#14B8A6", slot: 5 },
];
const ADD_TANKS = [
  { key: "wrda_pa", label: "WRDA PA", capL: 2000, colour: "#22C55E", match: ["WRDAPA"] },
  { key: "wrda_pn40", label: "PN40", capL: 2000, colour: "#D946EF", match: ["PN40"] },
  { key: "aea", label: "AEA", capL: 1000, colour: "#FCD34D", match: ["AEA"] },
  { key: "daraset_nc90", label: "NC90", capL: 1000, colour: "#38BDF8", match: ["NC90"] },
  { key: "retarder", label: "RETARD", capL: 1000, colour: "#14B8A6", match: ["RETARD"] },
  { key: "superplasticiser", label: "SUPER", capL: 1000, colour: "#EC4899", match: ["SUPER", "SPL"] },
];

const clamp = (v) => Math.max(0, Math.min(100, Number.isFinite(v) ? v : 0));
const tonnes = (kg) => `${(Number(kg || 0) / 1000).toFixed(1)} t`;
const litres = (l) => `${Math.round(Number(l || 0)).toLocaleString()} L`;
const normSku = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const toLitres = (i) => {
  const q = Number(i.quantity || 0);
  return (i.unit || "").toLowerCase() === "ml" ? q / 1000 : q;
};

export default function PlantLiveBoard({ plantId, batches, byLane }) {
  const { user, org } = useAuth();
  const canEdit = ["admin", "plant_manager", "batcher"].includes(user?.role);
  const canRename = ["admin", "plant_manager"].includes(user?.role);
  const [inv, setInv] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const invSeq = useRef(0);

  const refresh = useCallback(async () => {
    const seq = ++invSeq.current;
    try {
      const r = await api.get(`/inventory${plantId ? `?plant_id=${plantId}` : ""}`);
      if (seq === invSeq.current) setInv(r.data || []);
    } catch { /* keep last good levels on screen */ }
  }, [plantId]);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    window.addEventListener("batcher:refresh", refresh);
    return () => { clearInterval(t); window.removeEventListener("batcher:refresh", refresh); };
  }, [refresh]);

  // What the trucks currently batching have already weighed out — inventory only settles
  // when a load completes, so without this the bars sit frozen through the pour.
  const liveUsed = useMemo(() => {
    const used = {};
    for (const b of batches || []) {
      if (b.status !== "batching") continue;
      for (const [k, v] of Object.entries(b.actual_weights || {})) used[k] = (used[k] || 0) + (Number(v) || 0);
    }
    return used;
  }, [batches]);
  const cementUsedBySilo = useMemo(() => {
    const used = { A: 0, B: 0 };
    for (const b of batches || []) {
      if (b.status !== "batching") continue;
      const s = (b.cement_silo_id || "").toUpperCase().replace("SILO-", "").trim();
      if (s === "A" || s === "B") used[s] += Number(b.actual_weights?.cement || 0);
    }
    return used;
  }, [batches]);

  const itemFor = useCallback((cat) => {
    const cands = inv.filter((i) => (i.category || "") === cat);
    if (!cands.length) return null;
    return cands.reduce((best, i) => (Number(i.quantity || 0) > Number(best.quantity || 0) ? i : best), cands[0]);
  }, [inv]);

  const silos = useMemo(() => {
    const tagged = {};
    for (const i of inv) {
      if ((i.category || "") !== "cement") continue;
      const s = (i.silo || "").trim().toUpperCase();
      if (s === "A" || s === "B") tagged[s] = i;
    }
    const spare = inv.filter((i) => (i.category || "") === "cement"
      && !["A", "B"].includes((i.silo || "").trim().toUpperCase()));
    const build = (letter) => {
      const item = tagged[letter] || (letter === "B" ? spare[0] : undefined);
      const cap = Number(item?.capacity_kg) > 0 ? Number(item.capacity_kg) : SILO_CAP_KG;
      const qty = Math.max(0, Number(item?.quantity || 0) - (cementUsedBySilo[letter] || 0));
      return {
        id: item?.id, letter, name: (item?.name || `Silo ${letter}`).toUpperCase(),
        needsTag: !!item && !tagged[letter],
        low: Number(item?.low_stock_threshold || 0), qty, cap, unit: "kg",
        live: (cementUsedBySilo[letter] || 0) > 0, missing: !item,
      };
    };
    if (tagged.A || tagged.B) return [build("A"), build("B")];
    const any = itemFor("cement");
    // Nothing tagged yet: show the plant's cement as silo 1 and leave an empty silo 2
    // slot so a level can be typed straight into it from the panel.
    const cap = Number(any?.capacity_kg) > 0 ? Number(any.capacity_kg) : SILO_CAP_KG;
    return [{
      id: any?.id, letter: "A", name: (any?.name || "CEMENT").toUpperCase(),
      low: Number(any?.low_stock_threshold || 0),
      qty: Math.max(0, Number(any?.quantity || 0) - (liveUsed.cement || 0)), cap, unit: "kg",
      live: (liveUsed.cement || 0) > 0, missing: !any, untagged: true,
    }, build("B")];
  }, [inv, cementUsedBySilo, liveUsed, itemFor]);

  const bins = useMemo(() => BIN_DEFS.map((b) => {
    const item = itemFor(b.cat);
    if (!item) return null;
    const cap = Number(item.capacity_kg) > 0 ? Number(item.capacity_kg) : b.cap;
    return {
      id: item.id, key: b.key, name: (item.name || b.label).toUpperCase(), short: b.label, colour: b.colour,
      slot: b.slot,
      qty: Math.max(0, Number(item.quantity || 0) - (liveUsed[b.key] || 0)), cap, unit: "kg",
      low: Number(item.low_stock_threshold || 0), live: (liveUsed[b.key] || 0) > 0,
    };
  }).filter(Boolean), [itemFor, liveUsed]);

  const tanks = useMemo(() => ADD_TANKS.map((t) => {
    const chan = `additive_${t.key}`;
    const cands = inv.filter((i) => {
      const cat = (i.category || "").toLowerCase();
      if (cat === chan) return true;
      if (!(cat.startsWith("additive") || cat === "admixture")) return false;
      const hay = normSku(i.sku) + " " + normSku(i.name);
      return t.match.some((k) => hay.includes(k));
    });
    if (!cands.length) return null;
    const item = cands.reduce((best, i) => (toLitres(i) > toLitres(best) ? i : best), cands[0]);
    return {
      id: item.id, key: t.key, name: (item.name || t.label).toUpperCase(), short: t.label, colour: t.colour,
      qty: toLitres(item), cap: Number(item.capacity_kg) > 0 ? Number(item.capacity_kg) : t.capL, unit: "L",
      low: Number(item.low_stock_threshold || 0),
    };
  }).filter(Boolean), [inv]);

  const water = useMemo(() => {
    const item = inv.find((i) => normSku(i.sku) === "WATERTANK" || (i.category || "") === "water");
    return {
      id: item?.id, name: (item?.name || "WATER TANK").toUpperCase(), colour: "#38BDF8",
      qty: Math.max(0, Number(item?.quantity ?? WATER_CAP_L * 0.65) - (liveUsed.water || 0)),
      cap: Number(item?.capacity_kg) > 0 ? Number(item.capacity_kg) : WATER_CAP_L, unit: "L",
      low: Number(item?.low_stock_threshold || 0), missing: !item,
    };
  }, [inv, liveUsed]);

  const cementTotal = silos.reduce((s, x) => s + x.qty, 0);
  const isLow = (g) => (g.low > 0 ? g.qty <= g.low : clamp((g.qty / g.cap) * 100) < 15);
  const lanes = Object.keys(byLane || {});
  const activeBatch = lanes.map((l) => byLane[l]).find((b) => b && b.status === "batching");
  // A completed load is removed from the active lanes the instant it finishes, so the
  // lane map never shows it. Look at the full batch list for one that finished in the
  // last few seconds — that is what latches the bay light green.
  const doneBatch = (() => {
    const now = Date.now();
    let best = null, bestT = 0;
    for (const b of batches || []) {
      if (b.status !== "loaded" && b.status !== "complete") continue;
      const t = Date.parse(b.completed_at || b.ready_at || b.loaded_at || b.updated_at || 0) || 0;
      if (!t || now - t > 10000) continue;   // only loads finished in the last ~10s
      if (t > bestT) { bestT = t; best = b; }
    }
    return best;
  })();

  return (
    <div className="flex flex-col min-h-0 min-w-0 h-full bg-zinc-950 border border-zinc-800 overflow-hidden"
      data-testid="cockpit-plant-column">
      {/* ── TOP BAR · TOTAL STOCK ─────────────────────────────────────────── */}
      <div className="shrink-0 flex flex-wrap items-stretch gap-px bg-zinc-800 border-b border-zinc-800" data-testid="plant-board-totals">
        <div className="flex items-center gap-2 bg-black px-2.5 py-1.5">
          <span className="mono text-[8px] uppercase tracking-[0.3em] text-zinc-500">Plant · live</span>
        </div>
        <Total label="Cement" value={tonnes(cementTotal)} accent="#F57C00" testid="total-cement" />
        {bins.map((b) => (
          <Total key={b.key} label={b.name} value={tonnes(b.qty)} accent={b.colour} low={isLow(b)} testid={`total-${b.key}`} />
        ))}
        <Total label="Water" value={litres(water.qty)} accent="#38BDF8" testid="total-water" />
        {canEdit && (
          <button type="button" onClick={() => setEditOpen(true)} data-testid="plant-board-update-levels"
            title="Enter the current silo and bin levels"
            className="bg-black px-2.5 mono text-[9px] uppercase tracking-widest font-black text-primary-orange hover:bg-primary-orange hover:text-black inline-flex items-center gap-1 whitespace-nowrap">
            <SlidersHorizontal className="w-3 h-3" /> Update levels
          </button>
        )}
      </div>

      {/* ── THE PLANT · live levels painted onto the real plant ───────────── */}
      <div className="flex-1 min-h-0 bg-black flex items-start justify-center overflow-hidden">
        <div className="relative w-full" style={{ aspectRatio: "1264 / 848" }} data-testid="plant-board-scene">
        <img src={HERO_IMG} alt="Batch plant" draggable={false}
          className="absolute inset-0 w-full h-full object-fill select-none pointer-events-none" />

        {/* plant sign across the top band — the organisation's own name */}
        <div className="absolute inset-x-0 top-[0.2%] flex flex-col items-center justify-center px-4"
          data-testid="plant-board-sign">
          <span className="heading font-black uppercase leading-none text-white whitespace-nowrap"
            style={{ fontSize: "clamp(13px, 2.6vw, 34px)", letterSpacing: "0.12em",
                     textShadow: "0 0 18px rgba(245,124,0,.6), 0 2px 3px rgba(0,0,0,.95)" }}>
            {(org?.name || "ArmorBatch Global").toUpperCase()}
          </span>
          <span className="mono font-black uppercase text-primary-orange leading-none mt-0.5"
            style={{ fontSize: "clamp(6px, 0.62vw, 9px)", letterSpacing: "0.55em",
                     textShadow: "0 0 10px rgba(245,124,0,.8)" }}>
            Batch Portal · ArmorBatch Global
          </span>
        </div>

        {/* cement silos — level rises in the silo body, readout on the blank plate */}
        {silos.slice(0, 2).map((s, i) => {
          const box = SILO_BOX[i];
          const p = clamp((s.qty / s.cap) * 100);
          const low = isLow(s);
          return (
            <Fragment key={s.letter}>
              <div className="absolute overflow-hidden" data-testid={`plant-silo-${s.letter}`}
                title={`${s.name} · ${tonnes(s.qty)} of ${tonnes(s.cap)}`}
                style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%` }}>
                <div className="absolute inset-x-0 bottom-0 transition-[height] duration-700 mix-blend-multiply"
                  style={{ height: `${p}%`, background: low ? "rgba(220,38,38,.45)" : "rgba(245,124,0,.42)" }} />
                <div className="absolute inset-x-0 transition-[bottom] duration-700"
                  style={{ bottom: `${p}%`, height: 2, background: low ? "#EF4444" : "#F57C00" }} />
              </div>
              <div className={`absolute flex flex-col items-center justify-center text-center ${low ? "flash-status" : ""}`}
                style={{ left: `${box.plate.x}%`, top: `${box.plate.y}%`, width: `${box.plate.w}%`, height: `${box.plate.h}%`,
                         ...(low ? { "--flash-rgb": "239, 68, 68" } : {}) }}>
                <span className="mono text-[10px] uppercase tracking-tight text-zinc-700 leading-none w-full truncate px-0.5">SILO {s.letter}</span>
                <span className={`mono text-[17px] font-black tabular-nums leading-none ${low ? "text-red-600" : "text-zinc-900"}`}
                  data-testid={`plant-silo-pct-${s.letter}`}>{Math.round(p)}%</span>
                <span className="mono text-[10px] tabular-nums text-zinc-600 leading-none">{tonnes(s.qty)}</span>
              </div>
            </Fragment>
          );
        })}

        {/* aggregate bins — readout clipped to each steel bin */}
        <div className="absolute inset-0" data-testid="plant-board-bins">
          {bins.map((b) => {
            const box = BIN_BOX[b.slot];
            if (!box) return null;
            const p = clamp((b.qty / b.cap) * 100);
            const low = isLow(b);
            return (
              <div key={b.key} className="absolute -translate-x-1/2 -translate-y-1/2" data-testid={`plant-bin-${b.key}`}
                title={`${b.name} · ${tonnes(b.qty)} of ${tonnes(b.cap)}`}
                style={{ left: `${box.x}%`, top: `${box.y}%`, width: "9%" }}>
                <div className={`border bg-black/85 px-1 py-0.5 ${b.live ? "border-emerald-400" : low ? "border-red-500 flash-status" : "border-white/25"}`}
                  style={low && !b.live ? { "--flash-rgb": "239, 68, 68" } : undefined}>
                  <div className="mono text-[10px] font-black uppercase tracking-tight text-white leading-none truncate">{b.short}</div>
                  <div className="h-1.5 my-0.5 bg-zinc-900 border border-white/20 overflow-hidden">
                    <div className="h-full transition-[width] duration-700" style={{ width: `${p}%`, background: low ? "#DC2626" : b.colour }} />
                  </div>
                  <div className="flex items-baseline justify-between gap-0.5">
                    <span className={`mono text-[14px] font-black tabular-nums leading-none ${low ? "text-red-400" : b.live ? "text-emerald-400" : "text-white"}`}
                      data-testid={`plant-bin-pct-${b.key}`}>{Math.round(p)}%</span>
                    <span className="mono text-[9px] font-bold tabular-nums text-zinc-300 leading-none">{tonnes(b.qty)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* additive tanks on the gantry — litres */}
        <div className="absolute inset-0" data-testid="plant-board-tanks">
          {tanks.slice(0, TANK_BOX.length).map((t, i) => {
            const p = clamp((t.qty / t.cap) * 100);
            const low = isLow(t);
            return (
              <div key={t.key} className="absolute -translate-x-1/2" data-testid={`plant-tank-${t.key}`}
                title={`${t.name} · ${litres(t.qty)} of ${litres(t.cap)}`}
                style={{ left: `${TANK_BOX[i]}%`, top: "33.5%", width: "7%" }}>
                <div className={`border bg-black/85 px-0.5 py-0.5 text-center ${low ? "border-red-500 flash-status" : "border-white/25"}`}
                  style={low ? { "--flash-rgb": "239, 68, 68" } : undefined}>
                  <div className="mono text-[8px] font-black uppercase tracking-tight text-white leading-none truncate">{t.short}</div>
                  <div className="h-1.5 my-0.5 bg-zinc-900 overflow-hidden">
                    <div className="h-full transition-[width] duration-700" style={{ width: `${p}%`, background: low ? "#DC2626" : t.colour }} />
                  </div>
                  <div className={`mono text-[9px] font-bold tabular-nums leading-none ${low ? "text-red-400" : "text-zinc-200"}`}>{Math.round(t.qty)}L</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* loading bay — status on the wall above the truck */}
        <LoadingBay active={activeBatch} done={doneBatch} lane={activeBatch?.lane_id || doneBatch?.lane_id || lanes[0]} />
        </div>
      </div>

      <LevelEditor open={editOpen} onOpenChange={setEditOpen} canRename={canRename}
        rows={[...silos.map((s) => ({ ...s, group: "Cement silo" })),
               ...bins.map((b) => ({ ...b, group: "Aggregate bin" })),
               ...(water.id ? [{ ...water, group: "Water" }] : []),
               ...tanks.map((t) => ({ ...t, group: "Additive" }))]}
        onSaved={refresh} />
    </div>
  );
}

const Caption = ({ children }) => (
  <div className="mono text-[8px] uppercase tracking-[0.3em] text-zinc-500 shrink-0">{children}</div>
);

const Total = ({ label, value, accent, low, testid }) => (
  <div className={`bg-black px-2.5 py-1.5 min-w-[74px] flex-1 ${low ? "flash-status" : ""}`}
    style={low ? { "--flash-rgb": "239, 68, 68" } : undefined} data-testid={testid}>
    <div className="mono text-[8px] uppercase tracking-widest text-zinc-500 truncate">{label}</div>
    <div className="mono text-sm font-black tabular-nums leading-none mt-0.5"
      style={{ color: low ? "#F87171" : accent }}>{value}</div>
  </div>
);



/* Short two-tone "load complete" chime via Web Audio (silent if audio isn't armed yet). */
let _bayAudioCtx = null;
function playBayChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    _bayAudioCtx = _bayAudioCtx || new AC();
    const ctx = _bayAudioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const t0 = ctx.currentTime;
    [[880, 0], [1320, 0.14]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, t0 + at);
      gain.gain.exponentialRampToValueAtTime(0.28, t0 + at + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.28);
      osc.start(t0 + at);
      osc.stop(t0 + at + 0.3);
    });
  } catch { /* audio unavailable / blocked until a user gesture */ }
}

/* Loading bay — status badge sitting under the real chute, with a big traffic light.
   When a load reaches target it latches GREEN for a few seconds (so fast cycles still
   show the finish) and fires a one-shot chime + green flash so the loader notices. */
const BAY_HOLD_MS = 6000;
function LoadingBay({ active, done, lane }) {
  const b = active || done || null;
  const batched = b ? Object.values(b.actual_weights || {}).reduce((s, v) => s + (Number(v) || 0), 0) : 0;
  const target = b ? Object.values(b.target_weights || {}).reduce((s, v) => s + (Number(v) || 0), 0) : 0;
  // A load is "finished" only when it is actually COMPLETE — the batch is sealed
  // (ready_at, set by the load-complete monitor) or it has dropped to loaded/complete.
  // NOT when weighing hits target and NOT when "truck loading" merely starts.
  const liveFinished = !!done || (!!active && !!active.ready_at);
  const finishedId = liveFinished ? (b?.id || null) : null;

  const [snapshot, setSnapshot] = useState(null);   // held truck details for the green window
  const [greenUntil, setGreenUntil] = useState(0);
  const [flash, setFlash] = useState(false);
  const celebratedRef = useRef(null);
  const [, forceTick] = useState(0);

  // Fire once per finished batch: snapshot it, latch green, flash + chime.
  useEffect(() => {
    if (finishedId && celebratedRef.current !== finishedId && b) {
      celebratedRef.current = finishedId;
      setSnapshot({ truck_number: b.truck_number, quantity_m3: b.quantity_m3, mix_code: b.mix_code, batched, target, lane: b.lane_id || lane });
      setGreenUntil(Date.now() + BAY_HOLD_MS);
      setFlash(true);
      playBayChime();
      const ft = setTimeout(() => setFlash(false), 1100);
      return () => clearTimeout(ft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedId]);

  // Re-render once the green hold window elapses.
  useEffect(() => {
    if (!greenUntil) return;
    const left = greenUntil - Date.now();
    if (left <= 0) return;
    const t = setTimeout(() => forceTick((x) => x + 1), left + 60);
    return () => clearTimeout(t);
  }, [greenUntil]);

  const holding = greenUntil > 0 && Date.now() < greenUntil;
  const finished = liveFinished || holding;
  const state = finished ? "COMPLETE" : active ? "LOADING" : "IDLE";
  const tint = finished ? "#22C55E" : active ? "#F57C00" : "#A1A1AA";

  // While holding with no live batch on the lane, keep showing the finished truck.
  const disp = b
    ? { truck: b.truck_number, qty: b.quantity_m3, mix: b.mix_code, batched, target }
    : (holding && snapshot)
      ? { truck: snapshot.truck_number, qty: snapshot.quantity_m3, mix: snapshot.mix_code, batched: snapshot.batched, target: snapshot.target }
      : null;
  const dispLane = b ? (b.lane_id || lane) : (holding && snapshot ? snapshot.lane : lane);
  const dispP = disp && disp.target > 0 ? clamp((disp.batched / disp.target) * 100) : (finished ? 100 : 0);

  return (
    <div className="absolute left-[82%] -translate-x-1/2 top-[36%] w-[30%] min-w-[184px] flex flex-col items-center gap-2"
      data-testid="plant-loading-bay" data-state={state}>
      {/* one-shot green flash pulse on completion */}
      {flash && (
        <div className="pointer-events-none absolute -inset-6 rounded-2xl ring-4 ring-emerald-400 animate-ping" data-testid="plant-bay-flash" />
      )}
      <div className="relative">
        {flash && <span className="pointer-events-none absolute -inset-4 rounded-full bg-emerald-400/30 animate-ping" />}
        <TrafficLight state={state} size={30} />
      </div>
      <div className={`relative w-full min-w-0 border bg-black/80 backdrop-blur-sm px-2 py-1.5 text-center transition-[border-color] ${flash ? "ring-2 ring-emerald-400" : ""}`}
        style={{ borderColor: tint }}>
        <div className="mono text-[8px] uppercase tracking-[0.3em]" style={{ color: tint }} data-testid="plant-bay-lane">
          {dispLane || "lane-1"} · loading bay
        </div>
        <div className="heading text-xl font-black leading-none mt-0.5" style={{ color: tint }} data-testid="plant-bay-state">
          {finished ? "LOAD COMPLETE" : state}
        </div>
        {disp ? (
          <>
            <div className="mono text-[11px] font-black text-white mt-0.5 truncate" data-testid="plant-bay-truck">
              {disp.truck || "—"} · {Number(disp.qty || 0).toFixed(1)} m³ {disp.mix ? `· ${disp.mix}` : ""}
            </div>
            <div className="h-1.5 mt-1 bg-zinc-900 border border-white/20 overflow-hidden">
              <div className="h-full transition-[width] duration-700" style={{ width: `${dispP}%`, background: tint }} />
            </div>
            <div className="mono text-[8px] text-zinc-300 mt-0.5 tabular-nums">{tonnes(disp.batched)} of {tonnes(disp.target)} batched</div>
          </>
        ) : (
          <div className="mono text-[8px] uppercase tracking-widest text-zinc-400 mt-0.5">Waiting for a truck</div>
        )}
      </div>
    </div>
  );
}

/* Bay traffic light — red while a truck is loading, green when the load is done. */
function TrafficLight({ state, size = 14 }) {
  const lamps = [
    { key: "red", on: state === "LOADING", lit: "#EF4444", glow: "239,68,68" },
    { key: "amber", on: state === "IDLE", lit: "#F59E0B", glow: "245,158,11" },
    { key: "green", on: state === "COMPLETE", lit: "#22C55E", glow: "34,197,94" },
  ];
  const pad = Math.round(size * 0.34);
  const gap = Math.round(size * 0.3);
  const glowBlur = Math.round(size * 0.7);
  const glowSpread = Math.round(size * 0.18);
  return (
    <div className="shrink-0 rounded-lg border-2 border-zinc-700 bg-zinc-900/95 flex flex-col items-center shadow-[0_4px_14px_rgba(0,0,0,0.6)]"
      style={{ padding: pad, gap }}
      data-testid="plant-bay-traffic-light" data-state={state}>
      {lamps.map((l) => (
        <span key={l.key} data-testid={`bay-lamp-${l.key}`} data-on={l.on ? "1" : "0"}
          className={`block rounded-full border border-black/70 ${l.on ? "animate-pulse" : ""}`}
          style={{
            width: size, height: size,
            background: l.on ? l.lit : "#27272A",
            boxShadow: l.on
              ? `0 0 ${glowBlur}px ${glowSpread}px rgba(${l.glow},.85), inset 0 0 ${Math.round(size * 0.22)}px rgba(255,255,255,.35)`
              : "inset 0 0 4px rgba(0,0,0,.9)",
          }} />
      ))}
    </div>
  );
}

/* Manual level entry — tonnes or %, saved through the stock-adjust endpoint. */
function LevelEditor({ open, onOpenChange, rows, canRename, onSaved }) {
  const [mode, setMode] = useState("qty");       // qty | pct — applies to the level column
  const [vals, setVals] = useState({});
  const [caps, setCaps] = useState({});
  const [adds, setAdds] = useState({});
  const [names, setNames] = useState({});
  const [supplier, setSupplier] = useState("");
  const [docket, setDocket] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setVals({}); setCaps({}); setAdds({}); setNames({}); setSupplier(""); setDocket(""); }
  }, [open]);

  // tonnes/litres on screen → the storage unit the API expects (kg for bulk, L for tanks)
  const toStore = (r, n) => (r.unit === "kg" ? n * 1000 : n);
  const fromStore = (r, n) => (r.unit === "kg" ? n / 1000 : n);

  const save = async () => {
    const jobs = [];
    const num = (v) => (v !== undefined && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);
    for (const r of rows) {
      // A silo the plant hasn't got a stock record for yet: create it, then the
      // level/capacity typed against it lands on the new record.
      if (!r.id) {
        const lvl0 = num(vals[r.key ?? r.letter]);
        const cap0 = num(caps[r.key ?? r.letter]);
        if (lvl0 === null && cap0 === null) continue;
        jobs.push((async () => {
          const { data } = await api.post("/inventory", {
            sku: `CEM-SILO-${r.letter || "B"}-${Date.now().toString().slice(-5)}`,
            name: r.name || `Silo ${r.letter || "B"}`,
            category: "cement",
            unit: "kg",
            quantity: lvl0 === null ? 0 : (mode === "pct" ? ((cap0 !== null ? toStore(r, cap0) : r.cap) * clamp(lvl0)) / 100 : toStore(r, lvl0)),
            silo: r.letter || "B",
            capacity_kg: cap0 !== null ? toStore(r, cap0) : r.cap,
          });
          return data;
        })());
        continue;
      }
      if (r.needsTag) jobs.push(api.patch(`/inventory/${r.id}/display`, { silo: r.letter }));
      const lvl = num(vals[r.id]);
      if (lvl !== null) {
        const amount = mode === "pct" ? (r.cap * clamp(lvl)) / 100 : toStore(r, lvl);
        jobs.push(api.post(`/inventory/${r.id}/adjust`, { mode: "set", amount, reason: "Plant Live level entry" }));
      }
      const cap = num(caps[r.id]);
      if (cap !== null && cap > 0) {
        jobs.push(api.patch(`/inventory/${r.id}/display`, { capacity_kg: toStore(r, cap) }));
      }
      const add = num(adds[r.id]);
      if (add !== null && add > 0) {
        jobs.push(api.post(`/inventory/${r.id}/receipt`, {
          supplier: supplier.trim() || "Delivery",
          docket_ref: docket.trim(),
          quantity: toStore(r, add),
          notes: "Booked from the Plant Live board",
        }));
      }
      const nm = names[r.id];
      if (canRename && nm !== undefined && nm.trim() && nm.trim().toUpperCase() !== r.name) {
        jobs.push(api.patch(`/inventory/${r.id}/display`, { name: nm.trim() }));
      }
    }
    if (!jobs.length) { onOpenChange(false); return; }
    setBusy(true);
    // Saved one by one: a refused rename must not throw away the level corrections
    // and deliveries that did go through.
    const res = await Promise.allSettled(jobs);
    const failed = res.filter((x) => x.status === "rejected");
    setBusy(false);
    if (failed.length < jobs.length) {
      toast.success(`${jobs.length - failed.length} change${jobs.length - failed.length === 1 ? "" : "s"} saved`);
      onOpenChange(false);
      onSaved?.();
    }
    if (failed.length) toast.error(failed[0].reason?.response?.data?.detail || "Some changes could not be saved");
  };

  const groups = rows.reduce((acc, r) => { (acc[r.group] = acc[r.group] || []).push(r); return acc; }, {});
  const unitOf = (r) => (r.unit === "kg" ? "t" : "L");
  const rowKey = (r) => r.id || r.key || r.letter;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-surface border-industrial max-h-[88vh] overflow-y-auto" data-testid="plant-level-editor">
        <DialogHeader>
          <DialogTitle className="heading text-base font-black uppercase tracking-widest">Plant levels &amp; deliveries</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-1 mb-1">
          {[["qty", "Level in tonnes / litres"], ["pct", "Level in percent"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`level-mode-${k}`}
              className={`mono text-[9px] uppercase tracking-widest px-2 py-1 border ${
                mode === k ? "border-primary-orange text-primary-orange bg-primary-orange/10" : "border-industrial text-muted-industrial"
              }`}>{label}</button>
          ))}
          <span className="mono text-[9px] text-muted-industrial">Leave a box blank to change nothing</span>
        </div>

        {/* one supplier + docket for everything booked in this pass */}
        <div className="grid grid-cols-2 gap-2 border border-industrial/60 p-2">
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Delivery supplier</div>
            <input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. Cement Australia"
              data-testid="topup-supplier"
              className="w-full rounded-none bg-transparent border border-industrial px-2 py-1.5 mono text-xs text-ink outline-none focus:border-primary-orange" />
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial mb-1">Delivery docket no.</div>
            <input value={docket} onChange={(e) => setDocket(e.target.value)} placeholder="optional"
              data-testid="topup-docket"
              className="w-full rounded-none bg-transparent border border-industrial px-2 py-1.5 mono text-xs text-ink outline-none focus:border-primary-orange" />
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-[1fr,86px,86px,86px] gap-2 mono text-[8px] uppercase tracking-widest text-muted-industrial">
            <span>Silo / bin</span>
            <span className="text-right">Set level</span>
            <span className="text-right">Capacity</span>
            <span className="text-right">Top up (+)</span>
          </div>
          {Object.entries(groups).map(([g, items]) => (
            <div key={g}>
              <div className="mono text-[9px] uppercase tracking-[0.3em] text-muted-industrial mb-1">{g}</div>
              <div className="space-y-1">
                {items.map((r) => (
                  <div key={rowKey(r)} className="grid grid-cols-[1fr,86px,86px,86px] gap-2 items-center">
                    {canRename ? (
                      <div className="relative">
                        <input defaultValue={r.name} data-testid={`level-name-${r.id}`}
                          onChange={(e) => setNames((s) => ({ ...s, [r.id]: e.target.value }))}
                          className="w-full rounded-none bg-transparent border border-industrial pl-6 pr-2 py-1.5 mono text-xs text-ink uppercase outline-none focus:border-primary-orange" />
                        <Pencil className="w-3 h-3 absolute left-1.5 top-1/2 -translate-y-1/2 text-muted-industrial" />
                      </div>
                    ) : <div className="mono text-xs text-ink uppercase truncate">{r.name}</div>}
                    <Cell testid={`level-value-${rowKey(r)}`} unit={mode === "pct" ? "%" : unitOf(r)}
                      placeholder={mode === "pct" ? `${Math.round(clamp((r.qty / r.cap) * 100))}` : fromStore(r, r.qty).toFixed(r.unit === "kg" ? 1 : 0)}
                      onChange={(v) => setVals((s) => ({ ...s, [rowKey(r)]: v }))} />
                    <Cell testid={`level-cap-${rowKey(r)}`} unit={unitOf(r)}
                      placeholder={fromStore(r, r.cap).toFixed(r.unit === "kg" ? 1 : 0)}
                      onChange={(v) => setCaps((s) => ({ ...s, [rowKey(r)]: v }))} />
                    <Cell testid={`level-topup-${rowKey(r)}`} unit={unitOf(r)} placeholder="0"
                      disabled={!r.id} onChange={(v) => setAdds((s) => ({ ...s, [rowKey(r)]: v }))} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="mono text-[9px] text-muted-industrial leading-relaxed">
          <b>Set level</b> corrects what is on hand. <b>Top up</b> books a delivery on top of the current level and
          is logged as a goods-in receipt against the supplier above.
        </p>

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)} data-testid="level-cancel">Cancel</Button>
          <Button className="rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))]"
            disabled={busy} onClick={save} data-testid="level-save">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Cell = ({ testid, unit, placeholder, onChange, disabled }) => (
  <div className="relative">
    <input type="number" step="0.1" inputMode="decimal" data-testid={testid} placeholder={placeholder}
      disabled={disabled} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-none bg-transparent border border-industrial pl-2 pr-5 py-1.5 mono text-xs text-ink text-right outline-none focus:border-primary-orange" />
    <span className="mono text-[8px] uppercase text-muted-industrial absolute right-1 top-1/2 -translate-y-1/2">{unit}</span>
  </div>
);
